import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/pro-light-svg-icons';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';

export type TrueImpactLevelFeedbackSeverity = 'critical' | 'warning';

interface TrueImpactLevelFeedbackBoxProps {
  severity: TrueImpactLevelFeedbackSeverity;
  title: string;
  message: React.ReactNode;
  sourceText?: string;
  actionText?: string;
  dismissText?: string;
  note?: string | null;
  onExplain: () => unknown;
}

const FlagIcon = styled(FontAwesomeIcon)`
  flex-shrink: 0;
  font-size: 20px;
  line-height: 1;
  color: ${COLORS.warningDark};
`;

const WarningWrapper = styled.div`
  max-width: 800px;
  margin: ${SPACERS.large} auto 0;
  display: flex;
  gap: ${SPACERS.small};
  padding: ${SPACERS.medium};
  border-radius: 4px;
  border: 1px solid ${COLORS.warningSubdued};
  background-color: ${COLORS.warningBackground};
`;

const CriticalWrapper = styled(WarningWrapper)`
  border-color: ${COLORS.criticalSubdued};
  background-color: ${COLORS.criticalBackground};

  ${FlagIcon} {
    color: ${COLORS.critical};
  }
`;

const FlagBody = styled.div`
  flex: 1;
`;

const FlagMessage = styled(TrueImpactText)`
  margin-top: ${SPACERS.extraTiny};
`;

const FlagSource = styled(TrueImpactText)`
  margin-top: ${SPACERS.tiny};
`;

const FlagNote = styled(TrueImpactText)`
  margin-top: ${SPACERS.small};
`;

const FlagActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACERS.small};
  margin-top: ${SPACERS.small};
`;

const FlagLink = styled(TrueImpactText)`
  cursor: pointer;
`;

const TrueImpactLevelFeedbackBox: React.FC<TrueImpactLevelFeedbackBoxProps> = ({
  severity,
  title,
  message,
  sourceText,
  actionText = 'This is correct',
  dismissText = 'add a quick note to dismiss',
  note,
  onExplain,
}) => {
  const Wrapper = severity === 'critical' ? CriticalWrapper : WarningWrapper;

  return (
    <Wrapper>
      <FlagIcon icon={faCircleQuestion} />
      <FlagBody>
        <TrueImpactText variant="body-bold">{title}</TrueImpactText>
        <FlagMessage>{message}</FlagMessage>
        {sourceText && (
          <FlagSource variant="body-small" color="greyDark">
            {sourceText}
          </FlagSource>
        )}
        {note ? (
          <FlagNote variant="body-small-italic" color="greyDark">
            Your note: {note}
          </FlagNote>
        ) : (
          <FlagActions>
            <TrueImpactButton variant="secondary-small" onClick={onExplain}>
              {actionText}
            </TrueImpactButton>
            <FlagLink
              variant="body-small"
              color="ctaPrimary"
              onClick={onExplain}
            >
              {dismissText}
            </FlagLink>
          </FlagActions>
        )}
      </FlagBody>
    </Wrapper>
  );
};

export default TrueImpactLevelFeedbackBox;
